import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

export const UNDER_REVIEW_REASONS = [
  "Identity verification required",
  "Beneficiary or fund-use needs clarification",
  "Unusual donation activity",
  "Duplicate or copied content",
  "Reported by community",
  "Payout eligibility concern",
  "Other (see notes)",
];

export default function UnderReviewDialog({ trigger, onConfirm, testid = "under-review-dialog" }) {
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const toggle = (r) => setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const confirm = async () => {
    if (reasons.length === 0 && !notes.trim()) { toast.error("Select at least one reason or add a note."); return; }
    setLoading(true);
    try {
      await onConfirm(reasons, notes);
      setOpen(false); setReasons([]); setNotes("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-heading text-ff-blue">Put campaign under review</DialogTitle>
          <DialogDescription>Donations pause while under review. The organizer is notified with the reason(s) below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Reason(s)</Label>
          <div className="grid gap-2">
            {UNDER_REVIEW_REASONS.map((r) => (
              <label key={r} className="flex cursor-pointer items-start gap-2.5 text-sm text-ff-dark"
                data-testid={`ur-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Checkbox checked={reasons.includes(r)} onCheckedChange={() => toggle(r)} />
                <span>{r}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="ur-notes">Notes to the organizer</Label>
          <Textarea id="ur-notes" data-testid="ur-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="mt-1.5" placeholder="Explain what's needed to resolve the review…" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={confirm} disabled={loading} data-testid="ur-confirm" className="bg-ff-amber text-white hover:bg-ff-amber/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Put under review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
